import { type ElementType, type ReactNode, useRef } from 'react';

import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { cn } from '@/lib/utils';

export interface ParallaxProps {
  children: ReactNode;
  as?: ElementType;
  className?: string;
  /**
   * Travel as a fraction of the element's own height across the full scroll
   * pass. Positive drifts up (slower than scroll), negative drifts down.
   * Keep it subtle — 0.1–0.25 reads premium; more reads gimmicky.
   */
  speed?: number;
}

/**
 * Scroll-scrubbed vertical parallax. Depth without distraction — use on hero
 * imagery, background plates and pull-quotes. No-ops under reduced motion.
 */
export function Parallax({ children, as: Tag = 'div', className, speed = 0.15 }: ParallaxProps) {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      if (reduced || !ref.current) return;
      gsap.to(ref.current, {
        yPercent: -speed * 100,
        ease: 'none',
        scrollTrigger: {
          trigger: ref.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });
    },
    { scope: ref, dependencies: [reduced, speed] },
  );

  return (
    <Tag ref={ref} className={cn('will-change-transform', className)}>
      {children}
    </Tag>
  );
}
